import type { SearchParams } from '@/server/searchParams.ts';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters.ts';
import { createQueryString } from '@/utils/queryUtils.ts';
import { SubSourceType } from '@/app/common/starTrack/events/searchRequested.ts';
import { getSearchParam, getSearchParamsFromQueryString, removeParameters } from '@/app/common/urlParametersUtils.ts';
import { APP_NAME_SHORT } from '@/utils/appUtils.ts';
import type { Product } from '@/domainLogic/index.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { showsKidsFreeBadge } from '@/app/components/search/utils/buildDealCards/showsKidsFreeBadge.ts';
import { getApexNavParams } from '@/app/common/apexNavParams.ts';
import { parseApexFlexibleDays } from '@/app/common/apex/apexFlexibleDays.ts';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import { createSearchParamsFromUrl } from '@/app/common/createSearchParamsFromUrl.ts';
import { canUseDOM } from '@/app/common/utils/canUseDOM.ts';
import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { SearchPageData } from '@/app/search/createSearchPageData.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import { createClickId, createPreClickIdWithNoTimestamp, isValidDate } from '../../Utilities';

export const mealPlanAssoc: { [key: string]: string } = {
  '1': 'ACCOMODATION_ONLY',
  '2': 'ACCOMODATION_AND_BREAKFAST',
  '3': 'HALF_BOARD',
  '4': 'FULL_BOARD',
  '5': 'ALL_INCLUSIVE',
};

type CreateDeepLinkSearchParamsVCSearchIdProps = {
  product?: Product;
  searchParams: SearchParams;
  vcSearchId?: string;
};
export const createDeepLinkSearchParamsVCSearchId = ({
  product,
  searchParams,
  vcSearchId,
}: CreateDeepLinkSearchParamsVCSearchIdProps): SearchParams => {
  const apexPrice = product?.rate?.flags?.apex;
  // Don't send vcSearchId as for APEX it is not a valid Vacation Catalog id
  const vcSearchIdToUse = apexPrice ? undefined : vcSearchId || searchParams[URLParameters.VC_SEARCH_ID];
  const searchParamsToUse = removeParameters(searchParams, [URLParameters.VC_SEARCH_ID]);
  return {
    ...searchParamsToUse,
    ...(vcSearchIdToUse ? { [URLParameters.VC_SEARCH_ID]: vcSearchIdToUse } : {}),
  };
};

const createOpxDeepLink = (deepLink: string, clickId: string | undefined, userId: string | undefined): string => {
  const searchParams = createSearchParamsFromUrl();
  const searchId = searchParams[URLParameters.SEARCH_ID] || '';
  const clickIdToUse = clickId || createClickId(searchId, userId);
  const slicedSearchId = searchId.slice(searchId.length - 3);
  const trackingId = `${slicedSearchId}-${clickIdToUse}`;
  const backUrl = canUseDOM ? encodeURIComponent(window?.location?.href) : null;
  return `${deepLink}${trackingId ? `&trackingId=${trackingId}` : ''}${backUrl ? `&backUrl=${backUrl}` : ''}`;
};

export type CreateBaseHotelDetailDeeplinkProps = {
  product: Product;
  searchId: string;
  vcSearchId: string;
  dateFrom?: string;
  dateTo?: string;
  searchParams: SearchParams;
};
export const createBaseHotelDetailDeeplink = ({
  product,
  searchId,
  vcSearchId,
  dateFrom,
  dateTo,
  searchParams,
}: CreateBaseHotelDetailDeeplinkProps): string => {
  const adults = getSearchParam(searchParams, URLParameters.ADULTS);
  const direct = getSearchParam(searchParams, URLParameters.DIRECT);
  const prefilterAirlines = getSearchParam(searchParams, URLParameters.PRE_FILTER_AIRLINES);
  const isLiveSearch = isValidDate(new Date(dateFrom || ''));
  const instantSearchPriceContext = product?.rate?.context?.priceContext;
  const instantSearchSignature = product?.rate?.context?.signature;
  const apexPrice = product?.rate?.flags?.apex;

  // MPH-945: with a flexible-dates window applied, the deal departs on its own date rather than the
  // searched one, so the detail page has to be given that date everywhere — not just in the trip
  // dates it reads the stay from, but also in the searched-dates pair the rest of the page compares
  // against. The window itself is a results-page control and is dropped below.
  // `adddays` already opens its own departure window and turns the search non-live, which is what
  // keeps the switcher off it; the two are mutually exclusive, so the window loses here too rather
  // than rewriting the searched dates for a feature that never rendered.
  const hasFlexibleWindow =
    !searchParams[URLParameters.ADD_DAYS] && parseApexFlexibleDays(searchParams[URLParameters.APEX_FLEXIBLE_DAYS]) > 0;
  const dealDates: SearchParams =
    hasFlexibleWindow && dateFrom && dateTo
      ? { [URLParameters.DATE_FROM]: dateFrom, [URLParameters.DATE_TO]: dateTo }
      : {};

  // Don't send vcSearchId as for APEX it is not a valid Vacation Catalog id
  const searchParamsDeepLink = createDeepLinkSearchParamsVCSearchId({ product, searchParams, vcSearchId });
  const params: SearchParams = {
    ...(searchParamsDeepLink || {}),
    ...dealDates,
    [URLParameters.TRIP_DATES]: `${dateFrom?.replace(/-/g, '')}-${dateTo?.replace(/-/g, '')}`,
    [URLParameters.BF_SUB_SOURCE]:
      searchParamsDeepLink[URLParameters.BF_SUB_SOURCE] || SubSourceType.HotelDetailDefault,
    [URLParameters.EXT_REFERENCE_ID]: searchId,
    [URLParameters.EXT_REFERENCE_TYPE]: APP_NAME_SHORT,
    [URLParameters.ADULTS]: adults ? adults.toString() : `${ADULTS_DEFAULT}`,
    ...(isLiveSearch && direct ? { [URLParameters.STOPS]: '0;0' } : {}),
    ...(isLiveSearch && prefilterAirlines ? { [URLParameters.AIRLINE]: prefilterAirlines } : {}),
    ...(instantSearchPriceContext ? { [URLParameters.RATE_PRICE_CONTEXT]: instantSearchPriceContext } : {}),
    ...(instantSearchSignature ? { [URLParameters.RATE_PRICE_CONTEXT_SIGNATURE]: instantSearchSignature } : {}),
    ...(apexPrice ? { [URLParameters.RATE_APEX]: '1' } : {}),
  };

  const paramsUpdated = removeParameters(params, [URLParameters.LIMIT, URLParameters.APEX_FLEXIBLE_DAYS]);

  return createQueryString(paramsUpdated);
};

type FreshDeepLinkResponse = [opxLink?: string, link?: string];

type CreateFreshHotelDetailDeeplinkProps = {
  hotelIdChosen: string;
  dealCardChosen?: DealCard;
  searchPageData?: SearchPageData;
  pageType: PAGE_TYPE;
  clickId?: string;
  refreshSearch?: boolean;
  subSourceType?: SubSourceType;
  koProviderId?: string;
  commonData: CommonData;
  preClick: boolean;
};
export const createFreshDeeplink = ({
  hotelIdChosen,
  dealCardChosen,
  searchPageData,
  pageType,
  clickId,
  refreshSearch,
  subSourceType,
  koProviderId,
  commonData,
  preClick,
}: CreateFreshHotelDetailDeeplinkProps): FreshDeepLinkResponse => {
  const { userUid, isDesktopDevice } = commonData;
  const searchParamsFresh = getSearchParams();
  const dealCardToUse =
    dealCardChosen ||
    searchPageData?.productsData?.dealCards?.find((dealCard) => `${dealCard?.locales?.hotelId}` === hotelIdChosen);
  const deepLinkChosen = dealCardToUse?.deepLink;

  let searchParamsNextPage: SearchParams;

  if (pageType === PAGE_TYPE.SEARCH) {
    if (dealCardToUse?.product?.rate?.flags?.opx) {
      const opxDeepLink = preClick ? deepLinkChosen : createOpxDeepLink(deepLinkChosen || '', clickId, userUid);
      return [opxDeepLink, undefined];
    }

    const searchParamsDeepLink = createDeepLinkSearchParamsVCSearchId({
      product: dealCardToUse?.product,
      searchParams: searchParamsFresh,
    });
    const currentParams = {
      ...searchParamsDeepLink,
      ...getSearchParamsFromQueryString(deepLinkChosen),
    };
    const preClickParams: SearchParams = preClick
      ? {
          ...(clickId ? { [URLParameters.PRE_CLICK_ID]: clickId } : undefined),
          [URLParameters.RESULT_VIEWED_OTHER]: 'true',
        }
      : {
          ...(clickId ? { [URLParameters.CLICK_ID]: clickId } : undefined),
          [URLParameters.CLICK_ON_CARD]: 'true',
        };
    searchParamsNextPage = {
      ...removeParameters(currentParams, [
        // Back to results starts on page 1 whichever way the traveller goes back (MPH-1275).
        URLParameters.PAGE,
        URLParameters.OFFSET,
        // MPH-945: the deal's own dates travel in the deeplink; the window that found it does not.
        URLParameters.APEX_FLEXIBLE_DAYS,
        ...(isDesktopDevice ? [] : [URLParameters.VIEW_MODE_DESKTOP]),
      ]),
      [URLParameters.PAGE_TYPE]: PAGE_TYPE.HOTEL_DETAIL,
      ...preClickParams,
      ...(showsKidsFreeBadge(dealCardToUse) ? { [URLParameters.FROM_KIDS_FREE_BADGE]: 'true' } : undefined),
    };
  } else {
    // Switching hotels from a detail page (e.g. the map): carry the chosen hotel's APEX price-absorption
    // context onto the new detail URL, and clear the previous hotel's. (MPH-634)
    const apexNav = getApexNavParams(dealCardToUse);
    const paramsToRemove = (refreshSearch ? [URLParameters.SEARCH_ID] : []).concat([
      URLParameters.MODAL,
      URLParameters.ROOM_ID,
      URLParameters.MEALPLAN_ID,
      URLParameters.MEALPLAN_CODE,
      URLParameters.HIGHLIGHT_HOTEL_ID,
      URLParameters.CLICK_ID,
      URLParameters.RESULT_VIEWED_OTHER,
      URLParameters.CLICK_ON_CARD,
      URLParameters.FROM_KIDS_FREE_BADGE,
      ...apexNav.remove,
    ]);
    searchParamsNextPage = {
      ...removeParameters(searchParamsFresh, paramsToRemove),
      ...apexNav.params,
      [URLParameters.PAGE_TYPE]: PAGE_TYPE.HOTEL_DETAIL,
      ...(koProviderId ? { [URLParameters.KOPROVIDER_ID]: koProviderId } : undefined),
    };
  }

  if (subSourceType) {
    searchParamsNextPage[URLParameters.BF_SUB_SOURCE] = subSourceType;
  }

  return [undefined, `${BASE_PATH}/${hotelIdChosen}?${createQueryString(searchParamsNextPage)}`];
};

type GetCardHrefProps = {
  dealCard: DealCard;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
};
export const getCardHref = ({ dealCard, searchInfoData, commonData }: GetCardHrefProps) => {
  const { searchId } = searchInfoData;
  const { userUid } = commonData;
  const hotelIdChosen = `${dealCard?.product?.accommodation?.hotelId}`;
  const [opxLink, hotelDetailDeepLink] = createFreshDeeplink({
    hotelIdChosen,
    dealCardChosen: dealCard,
    pageType: PAGE_TYPE.SEARCH,
    clickId: createPreClickIdWithNoTimestamp(searchId, userUid),
    commonData,
    preClick: true,
  });

  return opxLink || hotelDetailDeepLink;
};
